import React, { useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

// ---------------------------------------------------------------------------
// Admin section cards
// ---------------------------------------------------------------------------
const sections = [
    {
        title: 'Temple Admin',
        description: 'Manage deities, practices, empowerment requests, and upload practice media.',
        to: '/temple/admin',
        available: true,
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
            </svg>
        ),
    },
    {
        title: 'Blog Editor',
        description: 'Write and publish posts for "The Path to the Robe" and "As It Was Taught to Me".',
        to: '/admin/blog',
        available: true,
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
        ),
    },
    {
        title: 'Dharma Library',
        description: 'Upload PDFs, HTML pages, and documents to the shared dharma works collection.',
        to: '/admin/library',
        available: true,
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
            </svg>
        ),
    },
    {
        title: 'Mantra Content',
        description: 'Add and edit the public mantra library entries. (Sutra references are view-only SuttaCentral proxies.)',
        to: '/admin/content',
        available: true,
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
        ),
    },
    {
        title: 'Avatar Library',
        description: 'Upload and manage user avatar images available for profile selection.',
        to: '/admin/avatars',
        available: true,
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
        ),
    },
    {
        title: 'Corpus Backups',
        description: 'View GFS backup archives, download manifests, and monitor DynamoDB export status.',
        to: '/admin/backups',
        available: true,
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
        ),
    },
    {
        title: 'Dharma Monitor',
        description: 'CDM integrity findings, on-demand scans, and Dharmadhara content suggestions awaiting review.',
        to: '/admin/dharma-monitor',
        available: true,
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
        ),
    },
    {
        title: 'User Management',
        description: 'View registered users, manage roles, and review account activity.',
        to: '/admin/users',
        available: false,
        icon: (
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
        ),
    },
];

// ---------------------------------------------------------------------------
// Admin portal
// ---------------------------------------------------------------------------
const Admin = () => {
    const { user, isAuthenticated, isLoading } = useAuth();

    useEffect(() => {
        document.body.style.backgroundImage = 'url("/images/adminPageBkg.jpg")';
        document.body.style.backgroundSize = 'cover';
        document.body.style.backgroundPosition = 'center';
        document.body.style.backgroundAttachment = 'fixed';
        return () => {
            document.body.style.backgroundImage = 'none';
            document.body.style.backgroundSize = '';
            document.body.style.backgroundPosition = '';
            document.body.style.backgroundAttachment = '';
        };
    }, []);

    // Wait for the auth check before deciding — redirecting while the session
    // is still loading bounces fresh sign-ins straight back to /auth.
    if (isLoading) {
        return null;
    }

    if (!isAuthenticated) {
        return <Navigate to="/auth" replace />;
    }

    if (!user?.isAdmin) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="bg-slate-900/80 backdrop-blur-sm rounded-xl p-10 text-center max-w-sm">
                    <p className="text-white text-lg mb-4">Administrator access required.</p>
                    <Link to="/" className="text-amber-400 hover:underline text-sm">Return home</Link>
                </div>
            </div>
        );
    }

    const firstName = user?.displayName?.split(' ')[0] || 'Admin';

    return (
        <div className="min-h-screen py-12 px-6">
            <div className="max-w-5xl mx-auto">

                {/* Header */}
                <div className="mb-10">
                    <div className="inline-block bg-slate-900/70 backdrop-blur-sm rounded-xl px-8 py-6">
                        <h1 className="text-3xl font-bold text-white mb-1">Admin Portal</h1>
                        <p className="text-gray-300 text-sm">
                            Welcome back, {firstName}. Buddha Wisdom administration.
                        </p>
                    </div>
                </div>

                {/* Section cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {sections.map(({ title, description, to, available, icon }) => {
                        const cardContent = (
                            <div className={`
                                h-full bg-slate-900/70 backdrop-blur-sm border rounded-xl p-6
                                flex flex-col gap-4 transition-all duration-200
                                ${available
                                    ? 'border-slate-600 hover:border-amber-500 hover:bg-slate-800/80 cursor-pointer'
                                    : 'border-slate-700 opacity-60 cursor-not-allowed'
                                }
                            `}>
                                <div className={`${available ? 'text-amber-400' : 'text-slate-500'}`}>
                                    {icon}
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <h2 className="text-white font-semibold text-lg">{title}</h2>
                                        {!available && (
                                            <span className="text-xs bg-slate-700 text-slate-400 px-2 py-0.5 rounded-full">
                                                Coming soon
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
                                </div>
                            </div>
                        );

                        return available ? (
                            <Link key={title} to={to} className="block h-full no-underline">
                                {cardContent}
                            </Link>
                        ) : (
                            <div key={title}>{cardContent}</div>
                        );
                    })}
                </div>

            </div>
        </div>
    );
};

export default Admin;
